/**
 * Natureland AI Chat Widget
 * A vanilla JS implementation of the wellness concierge chatbot
 */

(function () {
  'use strict';

  // Configuration - set via theme settings
  const API_KEY = window.NATURELAND_CHAT_API_KEY || '';
  const API_URL = 'https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash-exp:generateContent';

  // State
  let isOpen = false;
  let isLoading = false;
  let messages = [
    {
      role: 'ai',
      text: "Hello! I am your **Natureland Wellness Assistant**. \n\nI'm here to support your intimate wellness journey with expert guidance on our products and the **BloomSense App**. \n\nHow can I help you today?"
    }
  ];

  // System prompt for the AI
  const SYSTEM_PROMPT = `You are the "Wellness Concierge" for Natureland Health, a premium intimate wellness brand.

YOUR PERSONA:
- Tone: Empathetic, sophisticated, scientifically grounded, and warm. Like a knowledgeable holistic health practitioner.
- Style: Use Markdown for clarity (bold keywords, bullet points). Keep responses concise but helpful.

YOUR KNOWLEDGE BASE:
1. **Vaginal pH:** Explain that 3.8-4.5 is normal. Fluctuations are normal (cycle, intimacy, etc.).
2. **Boric Acid Suppositories:** Natureland uses 99.9% USP grade. Best for resetting pH, odor, and recurrent yeast/BV. Use nightly for 7-14 days.
3. **Information about BloomSense App:** Your companion app for logging pH, symptoms, and cycle. Helps identify triggers.
4. **Safety:** You are NOT a doctor. Always recommend seeing a professional for pain, fever, or pregnancy concerns.

Context: User is on the website. Available products: pH Test Strips, Boric Acid Suppositories.`;

  // Create widget HTML
  function createWidget() {
    // Check if widget already exists
    const existingWidget = document.getElementById('chat-widget');
    if (existingWidget) return;

    const widget = document.createElement('div');
    widget.id = 'chat-widget';
    widget.className = 'fixed bottom-6 right-6 z-[9999] font-sans';
    widget.style.cssText = 'transform: translate3d(0,0,0); will-change: transform; isolation: isolate; backface-visibility: hidden;';
    widget.innerHTML = `
      <!-- Closed State: FAB Button -->
      <button
        id="chat-fab"
        class="group relative flex items-center justify-center w-16 h-16 bg-gradient-to-tr from-primary to-pink-500 text-white rounded-full shadow-xl hover:shadow-2xl active:scale-95 transition-all duration-300"
        style="transform: translate3d(0,0,0); backface-visibility: hidden;"
        onmouseenter="this.style.transform='translate3d(0,0,0) scale(1.15)'"
        onmouseleave="this.style.transform='translate3d(0,0,0) scale(1)'"
      >
        <span class="material-icons text-3xl animate-pulse-slow group-hover:scale-110 group-hover:rotate-6 transition-all duration-300">auto_awesome</span>
        <span class="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full border-2 border-white dark:border-zinc-900"></span>

        <!-- Tooltip -->
        <div class="absolute right-full mr-4 bg-white dark:bg-zinc-800 text-deep-charcoal dark:text-gray-200 text-xs font-bold py-2 px-4 rounded-xl shadow-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
          Ask the Wellness Concierge
          <div class="absolute top-1/2 -right-1 w-2 h-2 bg-white dark:bg-zinc-800 transform rotate-45 -translate-y-1/2"></div>
        </div>
      </button>

      <!-- Open State: Chat Window -->
      <div
        id="chat-window"
        class="hidden bg-white dark:bg-zinc-900 w-[90vw] md:w-96 h-[600px] max-h-[80vh] rounded-[2rem] shadow-2xl flex flex-col overflow-hidden border border-gray-100 dark:border-zinc-800"
      >
        <!-- Header -->
        <div class="bg-gradient-to-r from-primary to-pink-600 p-6 text-white flex justify-between items-center shadow-md">
          <div class="flex items-center space-x-3">
            <div class="bg-white/20 p-2 rounded-full backdrop-blur-sm">
              <span class="material-icons text-xl">spa</span>
            </div>
            <div>
              <h3 class="font-bold text-sm tracking-wide">Wellness Concierge</h3>
              <div class="flex items-center space-x-1 mt-0.5">
                <span class="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse"></span>
                <p class="text-[10px] opacity-90 font-medium">Online Now</p>
              </div>
            </div>
          </div>
          <button id="chat-close" class="hover:bg-white/20 p-2 rounded-full transition-all">
            <span class="material-icons text-xl">close</span>
          </button>
        </div>

        <!-- Messages -->
        <div id="chat-messages" class="flex-grow p-4 overflow-y-auto bg-gray-50/50 dark:bg-black/20 space-y-6 scroll-smooth">
        </div>

        <!-- Input Area -->
        <div class="p-4 bg-white dark:bg-zinc-900 border-t border-gray-100 dark:border-zinc-800">
          <div class="relative flex items-center group">
            <input
              id="chat-input"
              type="text"
              placeholder="Ask about your intimate health..."
              class="w-full bg-gray-50 dark:bg-zinc-950 border border-gray-200 dark:border-zinc-800 rounded-full py-4 px-6 pr-14 text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all dark:text-white placeholder:text-gray-400"
            />
            <button
              id="chat-send"
              class="absolute right-2 w-10 h-10 bg-primary text-white rounded-full flex items-center justify-center shadow-md hover:bg-pink-600 disabled:opacity-50 disabled:hover:bg-primary transition-all active:scale-95"
            >
              <span class="material-icons text-sm ml-0.5">send</span>
            </button>
          </div>
          <p class="text-[10px] text-center text-gray-400 mt-2">
            For educational purposes only. Not medical advice.
          </p>
        </div>
      </div>
    `;

    document.body.appendChild(widget);
    initEventListeners();
    renderMessages();
  }

  // Simple markdown parser
  function parseMarkdown(text) {
    return text
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.+?)\*/g, '<em>$1</em>')
      .replace(/\n/g, '<br>')
      .replace(/^- (.+)$/gm, '<li>$1</li>')
      .replace(/(<li>.*<\/li>)/s, '<ul class="list-disc pl-4 my-2">$1</ul>');
  }

  // Render messages
  function renderMessages() {
    const container = document.getElementById('chat-messages');
    if (!container) return;

    container.innerHTML = messages.map(m => `
      <div class="flex ${m.role === 'user' ? 'justify-end' : 'justify-start items-start space-x-2'}">
        ${m.role === 'ai' ? `
          <div class="w-6 h-6 rounded-full bg-primary/10 flex-shrink-0 flex items-center justify-center mt-1">
            <span class="material-icons text-[10px] text-primary">auto_awesome</span>
          </div>
        ` : ''}
        <div class="max-w-[85%] p-4 text-sm leading-relaxed shadow-sm
          ${m.role === 'user'
        ? 'bg-primary text-white rounded-2xl rounded-tr-sm'
        : 'bg-white dark:bg-zinc-800 text-deep-charcoal dark:text-gray-200 rounded-2xl rounded-tl-sm border border-gray-100 dark:border-zinc-700'
      }">
          <div class="prose prose-sm dark:prose-invert max-w-none">
            ${parseMarkdown(m.text)}
          </div>
        </div>
      </div>
    `).join('');

    // Add loading indicator if needed
    if (isLoading) {
      container.innerHTML += `
        <div class="flex justify-start items-start space-x-2">
          <div class="w-6 h-6 rounded-full bg-primary/10 flex-shrink-0 flex items-center justify-center mt-1">
            <span class="material-icons text-[10px] text-primary">auto_awesome</span>
          </div>
          <div class="bg-white dark:bg-zinc-800 p-4 rounded-2xl rounded-tl-sm border border-gray-100 dark:border-zinc-700 shadow-sm">
            <div class="flex space-x-1 h-5 items-center">
              <div class="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style="animation-delay: 0ms"></div>
              <div class="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style="animation-delay: 150ms"></div>
              <div class="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style="animation-delay: 300ms"></div>
            </div>
          </div>
        </div>
      `;
    }

    // Scroll to bottom
    container.scrollTop = container.scrollHeight;
  }

  // Toggle chat window
  function toggleChat() {
    isOpen = !isOpen;
    const fab = document.getElementById('chat-fab');
    const window = document.getElementById('chat-window');

    if (isOpen) {
      fab.classList.add('hidden');
      window.classList.remove('hidden');
      window.classList.add('flex');
      document.getElementById('chat-input').focus();
    } else {
      fab.classList.remove('hidden');
      window.classList.add('hidden');
      window.classList.remove('flex');
    }
  }

  // Send message
  async function sendMessage() {
    const input = document.getElementById('chat-input');
    const userMsg = input.value.trim();

    if (!userMsg || isLoading) return;

    input.value = '';
    messages.push({ role: 'user', text: userMsg });
    isLoading = true;
    renderMessages();

    try {
      if (!API_KEY) {
        throw new Error('API key not configured');
      }

      const response = await fetch(`${API_URL}?key=${API_KEY}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          contents: [{
            parts: [{
              text: `${SYSTEM_PROMPT}\n\nUser: "${userMsg}"`
            }]
          }],
          generationConfig: {
            temperature: 0.7,
            maxOutputTokens: 1024,
          }
        })
      });

      if (!response.ok) {
        throw new Error('API request failed');
      }

      const data = await response.json();
      const aiResponse = data.candidates?.[0]?.content?.parts?.[0]?.text ||
        "I'm sorry, I'm finding it hard to connect right now. Please try again briefly.";

      messages.push({ role: 'ai', text: aiResponse });
    } catch (error) {
      console.error('Chat error:', error);
      messages.push({
        role: 'ai',
        text: "I apologize, I'm having a momentary connection issue. Please check your internet or try again."
      });
    } finally {
      isLoading = false;
      renderMessages();
    }
  }

  // Initialize event listeners
  function initEventListeners() {
    document.getElementById('chat-fab').addEventListener('click', toggleChat);
    document.getElementById('chat-close').addEventListener('click', toggleChat);
    document.getElementById('chat-send').addEventListener('click', sendMessage);
    document.getElementById('chat-input').addEventListener('keydown', function (e) {
      if (e.key === 'Enter') sendMessage();
    });
  }

  // Lazy initialize - only create widget after page is fully loaded
  // This improves initial page load performance
  let widgetInitialized = false;

  function lazyInit() {
    if (!widgetInitialized) {
      widgetInitialized = true;
      createWidget();
    }
  }

  // Initialize after page load completes (not blocking initial render)
  if (document.readyState === 'complete') {
    setTimeout(lazyInit, 100);
  } else {
    window.addEventListener('load', function () {
      setTimeout(lazyInit, 100);
    });
  }

})();
